/// <reference types="vite-plugin-svgr/client" />
import { GlobalMessages } from '@shared/localization/messages';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import DisketteIcon from '@/assets/diskette-icon.svg?react';
import { Button } from '@/components/atoms/Button';
import { ModalStateless } from '@/components/ModalStateless';
import Search from '@/components/molecules/Search';
import { ExportButtonsSelector } from '@/components/organism/DataTable/components/exportSelector';
import { eExportFormat } from '@/components/organism/DataTable/model';
import type { EstimationExportButtonsProps } from '@/components/organism/EstimationExportButtons';
import { useMessages } from '@/hooks/messages';
import { useEstimationExport } from '@/hooks/useEstimationExport';
import type { OmitSafely } from '@/types';

import { estimationItemDetailModalMessageDefinitions } from '../../EstimationItemDetailModal/messages';
import type { EstimationItemsGroupedTableProps } from '../../EstimationItemsGroupedTables';
import EstimationItemsGroupedTables from '../../EstimationItemsGroupedTables';
import { mapFromResourceEstimationItemsTableRecord } from '../../mappers/estimationItemsEditFormModelMapper';
import type {
  EstimationItemActionCallback,
  EstimationItemDetail,
  EstimationItemEditModel,
  EstimationItemsEditFormModel,
} from '../../model';
import { MainEstimationItemDetail } from './MainEstimationItemDetail';

type EstimateItemDetailModalProps = OmitSafely<
  EstimationItemsGroupedTableProps,
  'control' | 'onIncludeClicked' | 'searchText' | 'formModelEstimationItems'
> & {
  open: boolean;
  onClose: () => void;
  onSubmit: (formModel: EstimationItemsEditFormModel) => void;
  estimationId: string;
  estimationItem: EstimationItemDetail | undefined;
  estimationMethodologyId: string;
  onAssumptionsClicked: EstimationItemActionCallback;
  onCommentsClicked: EstimationItemActionCallback;
  exportProps: OmitSafely<EstimationExportButtonsProps, 'parentEstimationItemId'>;
};

const EstimationItemDetailModal: React.FC<EstimateItemDetailModalProps> = ({
  open,
  onClose,
  onSubmit,
  estimationId: _estimationId,
  estimationItem,
  estimationItems,
  estimationMethodologyId,
  onDetailsClicked,
  onAssumptionsClicked,
  onCommentsClicked,
  isReadOnly,
  methodologies,
  estimationModel,
  resourceDisplayNames,
  exportProps,
  ...remainingTableProps
}) => {
  const globalMessages = useMessages(GlobalMessages);
  const messages = useMessages(estimationItemDetailModalMessageDefinitions);
  const [searchText, setSearchText] = useState('');

  const estimationExporter = useEstimationExport(exportProps);
  const handleExportButtonClick = useCallback(
    (format: eExportFormat) => {
      if (estimationItem) {
        void estimationExporter(format, estimationItem.id);
      }
    },
    [estimationExporter, estimationItem],
  );

  const exportConfiguration = useMemo<eExportFormat[]>(
    () => [
      eExportFormat.EXCEL,
      eExportFormat.PDF,
      eExportFormat.CSV,
      eExportFormat.JSON,
      eExportFormat.PRINT,
    ],
    [],
  );

  const formModelInitial = useMemo(() => {
    if (!estimationItem) {
      return undefined;
    }
    const mainEstimationItem: EstimationItemEditModel = {
      id: estimationItem.id,
      name: estimationItem.name,
      description: estimationItem.description,
      phaseLeadId: estimationItem.phaseLeadId,
      methodologyId: estimationItem.hasEstimationModelItemParameters
        ? undefined
        : (estimationItem.methodologyId ?? estimationMethodologyId),
      isParentBasedCalculation: estimationItem.isParentBasedCalculation,
    };
    const subItems = estimationItems.map((x) =>
      mapFromResourceEstimationItemsTableRecord(x, estimationMethodologyId, estimationModel),
    );
    return {
      mainEstimationItem,
      estimationItems: subItems,
    };
  }, [estimationItem, estimationItems, estimationMethodologyId, estimationModel]);

  const { reset, watch, handleSubmit, control } = useForm<EstimationItemsEditFormModel>({
    defaultValues: formModelInitial,
  });

  const formModelEstimationItems = watch('estimationItems');

  useEffect(() => {
    reset(formModelInitial);
  }, [reset, formModelInitial]);

  return (
    <ModalStateless title={messages.title()} open={open} onClose={onClose} withoutButtons>
      {estimationItem && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <MainEstimationItemDetail
            control={control}
            estimationItem={estimationItem}
            methodologies={methodologies}
            estimationModel={estimationModel}
            resourceDisplayNames={resourceDisplayNames}
          />
          <div className='my-4 flex grid grid-cols-2 gap-4 sm:grid-cols-4'>
            <Button
              className='flex-1'
              variant='secondary'
              outlined
              onClick={() => onAssumptionsClicked(estimationItem.id)}
            >
              {messages.assumptionsButton()}
            </Button>
            <Button
              className='flex-1'
              variant='secondary'
              outlined
              onClick={() => onCommentsClicked(estimationItem.id)}
            >
              {messages.commentsButton()}
            </Button>
            <div className='hidden sm:block' />
            {isReadOnly ? (
              <div className='hidden sm:block' />
            ) : (
              <Button
                className='flex-1'
                type='submit'
                variant='warning'
                IconComponentLeft={DisketteIcon}
              >
                {messages.overrideOriginalEstimateButton()}
              </Button>
            )}
          </div>
          <hr className='my-8' />
          <h2>{messages.subItemsTitle()}</h2>
          <div className='mt-4 flex justify-end gap-2 rounded-xl'>
            <Search handleOnChange={setSearchText} />
            <ExportButtonsSelector
              exportConfiguration={exportConfiguration}
              onExportButtonClick={handleExportButtonClick}
            />
          </div>
          <EstimationItemsGroupedTables
            control={control}
            formModelEstimationItems={formModelEstimationItems}
            estimationItems={estimationItems}
            methodologies={methodologies}
            isReadOnly={isReadOnly}
            onAssumptionsClicked={onAssumptionsClicked}
            onCommentsClicked={onCommentsClicked}
            onDetailsClicked={onDetailsClicked}
            estimationModel={estimationModel}
            searchText={searchText}
            showEstimateColumn={true}
            {...remainingTableProps}
          />
          <div className='my-4 flex justify-end space-x-4'>
            <Button variant='secondary' outlined onClick={onClose}>
              {globalMessages.cancel()}
            </Button>
            {!isReadOnly && (
              <Button type='submit' variant='warning' IconComponentLeft={DisketteIcon}>
                {messages.overrideOriginalEstimateButton()}
              </Button>
            )}
          </div>
        </form>
      )}
    </ModalStateless>
  );
};

export default EstimationItemDetailModal;
